/**
 * Shared visual-asset utilities for Before/After Compare (PRD.235).
 *
 * Compare candidates come from two sources:
 *   1. Uploaded photos       — patient_files rows with kind='image'
 *   2. Saved drawings        — encounter_visual_charts with chart_type='drawing'
 *
 * This module abstracts both into a single CompareAsset shape and exposes the
 * rendering helpers needed by:
 *   - BeforeAfterCompare (compare picker + slider/side-by-side)
 *   - SavedBeforeAfterPreview (read-only saved cards)
 *   - timeline-previews (BeforeAfterThumbBody)
 *
 * Drawings render via the Konva helpers in SavedDrawPreview — see
 * generateThumbnail (composed data URL when CORS-safe) and
 * generateStrokesOverlay (transparent overlay for Firebase-hosted bases).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qPatientFilesByEncounter,
  qEncounterVisualCharts,
  qChartingTemplates,
  type PatientFileRow,
  type EncounterVisualChartRow,
  type ChartingTemplateRow,
} from '@helios/data-access';
import { generateThumbnail, generateStrokesOverlay } from './SavedDrawPreview';
import type { DrawingRecord } from './DrawCharting';

// ─── Types ───

export type SourceKind = 'photo' | 'drawing';

export interface CompareAsset {
  key: string; // `photo:${fileId}` or `drawing:${chartId}`
  source: SourceKind;
  fileId: string | null; // patient_files.id  (source='photo')
  chartId: string | null; // encounter_visual_charts.id  (source='drawing')
  createdAt: string | null;
  baseUrl: string | null; // photo URL, or drawing base image URL, or null for blank
  drawingRecord: DrawingRecord | null;
  baseType: string | null; // null for photo; 'blank'|'image'|'template' for drawing
  caption: string;
}

export interface Picked {
  source: SourceKind;
  fileId: string | null;
  chartId: string | null;
}

// ─── Picked helpers ───

export function assetToPicked(asset: CompareAsset): Picked {
  return { source: asset.source, fileId: asset.fileId, chartId: asset.chartId };
}

export function pickedKey(p: Picked | null): string | null {
  if (!p) return null;
  if (p.source === 'photo') return p.fileId ? `photo:${p.fileId}` : null;
  return p.chartId ? `drawing:${p.chartId}` : null;
}

/** Resolve one side of a before/after chart into a Picked. Falls back to the
 *  legacy file-only column when source columns are null on older rows. */
export function pickedFromChart(
  chart: EncounterVisualChartRow,
  side: 'before' | 'after',
): Picked | null {
  const source = (side === 'before' ? chart.before_source : chart.after_source) ?? 'photo';
  if (source === 'drawing') {
    const chartId = side === 'before' ? chart.before_chart_id : chart.after_chart_id;
    return chartId ? { source: 'drawing', fileId: null, chartId } : null;
  }
  const fileId = side === 'before' ? chart.before_file_id : chart.after_file_id;
  return fileId ? { source: 'photo', fileId, chartId: null } : null;
}

/** True if a chart row has any reference on either side. */
export function chartHasAnyRef(c: EncounterVisualChartRow): boolean {
  return !!(c.before_file_id || c.after_file_id || c.before_chart_id || c.after_chart_id);
}

// ─── Asset aggregation hook ───

export interface UseCompareAssetsResult {
  assets: CompareAsset[];
  assetByKey: Map<string, CompareAsset>;
  fileRows: PatientFileRow[];
  drawingRows: EncounterVisualChartRow[];
}

/** Aggregate photos + drawings for an encounter into the unified asset list. */
export function useCompareAssets(
  patientId: string,
  encounterId: string,
  orgId: string,
): UseCompareAssetsResult {
  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(
    qPatientFilesByEncounter(patientId, encounterId),
  );
  const { data: drawingRows = [] } = usePowerSyncQuery<EncounterVisualChartRow>(
    qEncounterVisualCharts(encounterId, 'drawing'),
  );
  const { data: templateRows = [] } = usePowerSyncQuery<ChartingTemplateRow>(
    qChartingTemplates(orgId),
  );

  const fileById = useMemo(() => {
    const m = new Map<string, PatientFileRow>();
    for (const f of fileRows) m.set(f.id, f);
    return m;
  }, [fileRows]);
  const templateById = useMemo(() => {
    const m = new Map<string, ChartingTemplateRow>();
    for (const t of templateRows) m.set(t.id, t);
    return m;
  }, [templateRows]);

  const assets = useMemo<CompareAsset[]>(() => {
    const photos: CompareAsset[] = fileRows
      .filter((f) => f.kind === 'image' && f.url)
      .map((f) => ({
        key: `photo:${f.id}`,
        source: 'photo' as const,
        fileId: f.id,
        chartId: null,
        createdAt: f.created_at,
        baseUrl: f.url,
        drawingRecord: null,
        baseType: null,
        caption: 'Photo',
      }));

    const drawings: CompareAsset[] = [];
    for (const row of drawingRows) {
      if (!row.drawing_data) continue;
      let record: DrawingRecord | null = null;
      try {
        record = JSON.parse(row.drawing_data);
      } catch {
        continue;
      }
      if (!record) continue;

      let baseUrl: string | null = null;
      if (record.baseType === 'image' && record.baseImageId) {
        baseUrl = fileById.get(record.baseImageId)?.url ?? null;
      } else if (record.baseType === 'template' && record.templateId) {
        baseUrl = templateById.get(record.templateId)?.image_url ?? null;
      }

      const caption =
        record.baseType === 'image'
          ? 'Drawing (Image)'
          : record.baseType === 'template'
            ? 'Drawing (Template)'
            : 'Drawing';

      drawings.push({
        key: `drawing:${row.id}`,
        source: 'drawing',
        fileId: null,
        chartId: row.id,
        createdAt: row.created_at,
        baseUrl,
        drawingRecord: record,
        baseType: record.baseType,
        caption,
      });
    }

    return [...photos, ...drawings].sort((a, b) => {
      const aa = a.createdAt ?? '';
      const bb = b.createdAt ?? '';
      return bb.localeCompare(aa);
    });
  }, [fileRows, drawingRows, fileById, templateById]);

  const assetByKey = useMemo(() => {
    const m = new Map<string, CompareAsset>();
    for (const a of assets) m.set(a.key, a);
    return m;
  }, [assets]);

  return { assets, assetByKey, fileRows, drawingRows };
}

// ─── Rendering ───

export interface ResolvedAssetUrls {
  baseUrl: string | null;
  strokesUrl: string | null;
  loading: boolean;
}

/** Resolve a CompareAsset into URLs suitable for rendering.
 *
 * Photos: synchronously returns the file URL.
 * Drawings:
 *   - Firebase-hosted base image: returns base URL + a generated transparent
 *     strokes overlay (layered <img>+<img>) because toDataURL is CORS-blocked.
 *   - All other drawings: returns a single composited data URL.
 */
export function useResolvedAssetUrls(asset: CompareAsset | null): ResolvedAssetUrls {
  const [strokesUrl, setStrokesUrl] = useState<string | null>(null);
  const [composedUrl, setComposedUrl] = useState<string | null>(null);
  const generatedForRef = useRef<string | null>(null);

  const isFirebaseDrawing =
    asset?.source === 'drawing' &&
    (asset.baseType === 'image' || asset.baseType === 'template') &&
    !!asset.baseUrl &&
    asset.baseUrl.includes('firebasestorage.googleapis.com');

  useEffect(() => {
    if (!asset || asset.source !== 'drawing' || !asset.drawingRecord) {
      setStrokesUrl(null);
      setComposedUrl(null);
      generatedForRef.current = null;
      return;
    }
    const key = `${asset.key}:${asset.baseType ?? ''}:${asset.baseUrl ?? ''}:${asset.drawingRecord.strokes.length}`;
    if (generatedForRef.current === key) return;
    generatedForRef.current = key;

    if (isFirebaseDrawing) {
      setComposedUrl(null);
      generateStrokesOverlay(asset.drawingRecord)
        .then(setStrokesUrl)
        .catch(() => setStrokesUrl(null));
    } else {
      setStrokesUrl(null);
      generateThumbnail(asset.drawingRecord, asset.baseUrl)
        .then(setComposedUrl)
        .catch(() => setComposedUrl(null));
    }
  }, [asset, isFirebaseDrawing]);

  if (!asset) return { baseUrl: null, strokesUrl: null, loading: false };

  if (asset.source === 'photo') {
    return { baseUrl: asset.baseUrl, strokesUrl: null, loading: !asset.baseUrl };
  }
  if (isFirebaseDrawing) {
    return { baseUrl: asset.baseUrl, strokesUrl, loading: !strokesUrl };
  }
  return { baseUrl: composedUrl, strokesUrl: null, loading: !composedUrl };
}

const ABSOLUTE_FILL: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};

/** Inline renderer for a CompareAsset. Handles photos and drawings (layered
 *  when needed). When the asset is null, renders an empty pane. */
export function AssetImage({
  asset,
  alt,
  objectFit = 'contain',
  loadingFallback,
}: {
  asset: CompareAsset | null;
  alt: string;
  objectFit?: 'contain' | 'cover';
  loadingFallback?: React.ReactNode;
}) {
  const { baseUrl, strokesUrl, loading } = useResolvedAssetUrls(asset);

  if (!asset || loading || !baseUrl) {
    if (loadingFallback !== undefined) return <>{loadingFallback}</>;
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
          fontSize: 'var(--font-size-xs)',
          background: 'var(--bg-surface-2)',
        }}
      >
        {asset ? 'Loading…' : ''}
      </div>
    );
  }

  if (strokesUrl) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', background: '#fff' }}>
        <img src={baseUrl} alt={alt} style={{ ...ABSOLUTE_FILL, objectFit }} />
        <img src={strokesUrl} alt="" style={{ ...ABSOLUTE_FILL, objectFit }} />
      </div>
    );
  }

  return (
    <img
      src={baseUrl}
      alt={alt}
      style={{
        width: '100%',
        height: '100%',
        objectFit,
        display: 'block',
        background: asset.source === 'drawing' ? '#fff' : undefined,
      }}
    />
  );
}
